/* eslint-disable no-nested-ternary */
import { cva } from "class-variance-authority";

import { Tooltip } from "@/src/components/design-system/Tooltip/Tooltip";
import { Badge } from "@/src/components/design-system/Badge/Badge";
import { cn } from "@/src/utils/tailwind";

/**
 * The context a score was created in — its "level". A score's meaning depends
 * on this context, so every surface that shows a score must show its level
 * (LFE-10596; see the ScoreTag Storybook story for the full guidance).
 *
 * Only `trace` and `observation` are derivable from data today. `session` and
 * `experiment` are defined ahead of the backend writing session_id /
 * experiment_id onto scores, so the coding is stable when they arrive.
 */
export type ScoreLevel = "trace" | "observation" | "session" | "experiment";

export const SCORE_LEVEL_LABELS: Record<ScoreLevel, string> = {
  trace: "Trace",
  observation: "Observation",
  session: "Session",
  experiment: "Experiment",
};

const SCORE_LEVEL_DESCRIPTIONS: Record<ScoreLevel, string> = {
  trace: "Trace-level score: the whole trace was the evaluation context",
  observation:
    "Observation-level score: a single observation was the evaluation context",
  session: "Session-level score: a session was the evaluation context",
  experiment:
    "Experiment-level score: an experiment run was the evaluation context",
};

/**
 * Stored scores carry no explicit level field — level is derived from which
 * context id is set, narrowest first (an observation score also carries its
 * traceId). Callers with an implicit trace context (e.g. the trace detail
 * view) may pass just `observationId`; the fallback is trace.
 */
export const scoreLevelFromScore = (score: {
  observationId?: string | null;
  traceId?: string | null;
  sessionId?: string | null;
  datasetRunId?: string | null;
}): ScoreLevel =>
  score.observationId != null
    ? "observation"
    : score.traceId != null
      ? "trace"
      : score.sessionId != null
        ? "session"
        : score.datasetRunId != null
          ? "experiment"
          : "trace";

// The global score-level color coding: one hue per level, used identically on
// every surface. Hue pairs live in the design-system Badge and globals.css:
// observation=blue, trace=violet, session=teal, experiment=yellow.
const scoreTagColors: Record<
  ScoreLevel,
  "blue" | "violet" | "teal" | "yellow"
> = {
  observation: "blue",
  trace: "violet",
  session: "teal",
  experiment: "yellow",
};

const scoreDotVariants = cva("inline-block size-2 shrink-0 rounded-full", {
  variants: {
    level: {
      observation: "bg-dark-blue",
      trace: "bg-dark-violet",
      session: "bg-dark-teal",
      experiment: "bg-dark-yellow",
    },
  },
});

export interface ScoreTagProps {
  level: ScoreLevel;
  /**
   * Dense-view variant (trace tree / timeline rows): a color dot carrying the
   * level name via tooltip + aria-label instead of a visible word.
   */
  compact?: boolean;
}

/**
 * Tags a score with the level it was created at, using the global score-level
 * color coding. Never color-alone: the full variant shows the level word, the
 * compact dot carries it via tooltip and aria-label.
 */
export const ScoreTag = ({ level, compact = false }: ScoreTagProps) => {
  if (compact) {
    return (
      <Tooltip label={SCORE_LEVEL_DESCRIPTIONS[level]}>
        {({ getTriggerProps }) => (
          <span
            {...getTriggerProps()}
            role="img"
            aria-label={SCORE_LEVEL_DESCRIPTIONS[level]}
            className={cn(scoreDotVariants({ level }))}
          />
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip label={SCORE_LEVEL_DESCRIPTIONS[level]}>
      {({ getTriggerProps }) => (
        <span {...getTriggerProps()}>
          <Badge
            color={scoreTagColors[level]}
            size="sm"
            text={SCORE_LEVEL_LABELS[level]}
          />
        </span>
      )}
    </Tooltip>
  );
};
